import React, { useState } from 'react';
import { Question, ConditionalLogic } from '../types/Survey';
import { Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import './ConditionalLogicBuilder.css';

interface ConditionalLogicBuilderProps {
  question: Question;
  allQuestions: Question[];
  onLogicChange: (logic: ConditionalLogic[]) => void;
}

const ConditionalLogicBuilder: React.FC<ConditionalLogicBuilderProps> = ({
  question,
  allQuestions,
  onLogicChange
}) => {
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingLogic, setEditingLogic] = useState<ConditionalLogic | null>(null);
  const [formData, setFormData] = useState({
    sourceQuestionId: '',
    condition: 'equals' as ConditionalLogic['condition'],
    value: '',
    action: 'skip_to' as ConditionalLogic['action'],
    targetQuestionId: ''
  });

  const logic = question.conditionalLogic || [];
  
  // 현재 질문보다 앞에 있는 질문들만 소스로 선택 가능
  const availableSourceQuestions = allQuestions.filter(q => {
    const currentIndex = allQuestions.findIndex(aq => aq.id === question.id);
    const sourceIndex = allQuestions.findIndex(aq => aq.id === q.id);
    return sourceIndex < currentIndex;
  });

  // 현재 질문보다 뒤에 있는 질문들만 타겟으로 선택 가능
  const availableTargetQuestions = allQuestions.filter(q => {
    const currentIndex = allQuestions.findIndex(aq => aq.id === question.id);
    const targetIndex = allQuestions.findIndex(aq => aq.id === q.id);
    return targetIndex > currentIndex;
  });

  const conditionOptions = [
    { value: 'equals', label: '같다' },
    { value: 'not_equals', label: '같지 않다' },
    { value: 'contains', label: '포함한다' },
    { value: 'not_contains', label: '포함하지 않는다' },
    { value: 'greater_than', label: '보다 크다' },
    { value: 'less_than', label: '보다 작다' },
    { value: 'is_empty', label: '비어있다' },
    { value: 'is_not_empty', label: '비어있지 않다' }
  ];

  const actionOptions = [
    { value: 'skip_to', label: '특정 질문으로 건너뛰기' },
    { value: 'show_question', label: '이 질문 표시' },
    { value: 'hide_question', label: '이 질문 숨기기' },
    { value: 'end_survey', label: '설문 종료' }
  ];

  const resetForm = () => {
    setFormData({
      sourceQuestionId: '',
      condition: 'equals',
      value: '',
      action: 'skip_to',
      targetQuestionId: ''
    });
    setEditingLogic(null);
  };

  const handleAddLogic = () => {
    setShowBuilder(true);
    resetForm();
  };

  const handleEditLogic = (logicItem: ConditionalLogic) => {
    setEditingLogic(logicItem);
    setFormData({
      sourceQuestionId: logicItem.sourceQuestionId,
      condition: logicItem.condition,
      value: logicItem.value,
      action: logicItem.action,
      targetQuestionId: logicItem.targetQuestionId || ''
    });
    setShowBuilder(true);
  };

  const handleSaveLogic = () => {
    // Clean the value - remove any HTML tags if present
    let cleanValue = formData.value;
    if (typeof cleanValue === 'string' && cleanValue.includes('<')) {
      cleanValue = cleanValue.replace(/<[^>]*>/g, '');
    }
    
    const newLogicItem: ConditionalLogic = {
      id: editingLogic?.id || uuidv4(),
      sourceQuestionId: formData.sourceQuestionId,
      condition: formData.condition,
      value: cleanValue,
      action: formData.action,
      targetQuestionId: formData.action === 'skip_to' ? formData.targetQuestionId : undefined
    };

    let newLogic: ConditionalLogic[];
    if (editingLogic) {
      newLogic = logic.map(l => l.id === editingLogic.id ? newLogicItem : l);
    } else {
      newLogic = [...logic, newLogicItem];
    }

    onLogicChange(newLogic);
    setShowBuilder(false);
    resetForm();
  };

  const handleDeleteLogic = (logicId: string) => {
    if (window.confirm('이 조건부 로직을 삭제하시겠습니까?')) {
      const newLogic = logic.filter(l => l.id !== logicId);
      onLogicChange(newLogic);
    }
  };

  const getSourceQuestionName = (questionId: string) => {
    const sourceQuestion = allQuestions.find(q => q.id === questionId);
    if (!sourceQuestion) {
      // 질문을 찾을 수 없으면 삭제된 질문으로 표시
      return '삭제된 질문';
    }
    
    // 질문 텍스트가 비어있으면 질문 번호 표시
    if (!sourceQuestion.question || sourceQuestion.question.trim() === '') {
      const questionIndex = allQuestions.findIndex(q => q.id === questionId);
      return `질문 ${questionIndex + 1}`;
    }
    
    return sourceQuestion.question;
  };

  const getTargetQuestionName = (questionId: string) => {
    const targetQuestion = allQuestions.find(q => q.id === questionId);
    if (!targetQuestion) {
      // 질문을 찾을 수 없으면 삭제된 질문으로 표시
      return '삭제된 질문';
    }
    
    // 질문 텍스트가 비어있으면 질문 번호 표시
    if (!targetQuestion.question || targetQuestion.question.trim() === '') {
      const questionIndex = allQuestions.findIndex(q => q.id === questionId);
      return `질문 ${questionIndex + 1}`;
    }
    
    return targetQuestion.question;
  };

  const getConditionLabel = (condition: ConditionalLogic['condition']) => {
    return conditionOptions.find(opt => opt.value === condition)?.label || condition;
  };

  const getActionLabel = (action: ConditionalLogic['action']) => {
    return actionOptions.find(opt => opt.value === action)?.label || action;
  };

  const isFormValid = () => {
    if (!formData.sourceQuestionId || !formData.condition || !formData.action) {
      return false;
    }
    
    if (formData.action === 'skip_to' && !formData.targetQuestionId) {
      return false;
    }

    if (['equals', 'not_equals', 'contains', 'not_contains', 'greater_than', 'less_than'].includes(formData.condition) && !formData.value) {
      return false;
    }

    return true;
  };

  const getSourceQuestionOptions = (sourceQuestionId: string) => {
    const sourceQuestion = allQuestions.find(q => q.id === sourceQuestionId);
    if (!sourceQuestion) return [];

    switch (sourceQuestion.type) {
      case 'multiple-choice':
      case 'dropdown':
        return sourceQuestion.options || [];
      case 'checkbox':
        return sourceQuestion.options || [];
      case 'rating':
      case 'star-rating':
        const maxRating = sourceQuestion.type === 'star-rating' 
          ? (sourceQuestion.starCount || 5) 
          : (sourceQuestion.maxRating || 5);
        return Array.from({ length: maxRating }, (_, i) => String(i + 1));
      default:
        return [];
    }
  };

  return (
    <div className="conditional-logic-builder">
      <div className="logic-header">
        <h4>조건부 로직</h4>
        <button 
          type="button" 
          className="btn btn-sm btn-outline"
          onClick={handleAddLogic}
        >
          + 로직 추가
        </button>
      </div>

      {logic.length > 0 && (
        <div className="logic-list">
          {logic.map((logicItem, index) => (
            <div key={logicItem.id} className="logic-item">
              <div className="logic-summary">
                <span className="logic-index">{index + 1}</span>
                <div className="logic-description">
                  만약 "<strong>{getSourceQuestionName(logicItem.sourceQuestionId)}</strong>" 
                  이(가) <strong>{getConditionLabel(logicItem.condition)}</strong>
                  {logicItem.value && (
                    <>
                      {' "'}
                      <strong>
                        {typeof logicItem.value === 'string' && logicItem.value.includes('<') 
                          ? logicItem.value.replace(/<[^>]*>/g, '') // Remove HTML tags
                          : logicItem.value}
                      </strong>
                      {'"'}
                    </>
                  )}이면
                  <br />
                  <strong>{getActionLabel(logicItem.action)}</strong>
                  {logicItem.targetQuestionId && ` "${getTargetQuestionName(logicItem.targetQuestionId)}"`}
                </div>
              </div>
              <div className="logic-actions">
                <button
                  type="button"
                  className="btn-icon"
                  onClick={() => handleEditLogic(logicItem)}
                  title="편집"
                >
                  ✏️
                </button>
                <button
                  type="button"
                  className="btn-icon delete"
                  onClick={() => handleDeleteLogic(logicItem.id)}
                  title="삭제"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showBuilder && (
        <div className="logic-builder-modal">
          <div className="modal-overlay" onClick={() => setShowBuilder(false)} />
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingLogic ? '로직 편집' : '로직 추가'}</h3>
              <button 
                type="button"
                className="btn-close"
                onClick={() => setShowBuilder(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>조건 질문</label>
                <select
                  value={formData.sourceQuestionId}
                  onChange={(e) => setFormData({ ...formData, sourceQuestionId: e.target.value })}
                >
                  <option value="">질문을 선택하세요</option>
                  {availableSourceQuestions.map(q => (
                    <option key={q.id} value={q.id}>
                      {q.question}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>조건</label>
                <select
                  value={formData.condition}
                  onChange={(e) => setFormData({ ...formData, condition: e.target.value as ConditionalLogic['condition'] })}
                >
                  {conditionOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {!['is_empty', 'is_not_empty'].includes(formData.condition) && (
                <div className="form-group">
                  <label>비교값</label>
                  {getSourceQuestionOptions(formData.sourceQuestionId).length > 0 ? (
                    <select
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    >
                      <option value="">값을 선택하세요</option>
                      {getSourceQuestionOptions(formData.sourceQuestionId).map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                      placeholder="비교할 값을 입력하세요"
                    />
                  )}
                </div>
              )}

              <div className="form-group">
                <label>실행 동작</label>
                <select
                  value={formData.action}
                  onChange={(e) => setFormData({ ...formData, action: e.target.value as ConditionalLogic['action'] })}
                >
                  {actionOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {formData.action === 'skip_to' && (
                <div className="form-group">
                  <label>이동할 질문</label>
                  <select
                    value={formData.targetQuestionId}
                    onChange={(e) => setFormData({ ...formData, targetQuestionId: e.target.value })}
                  >
                    <option value="">질문을 선택하세요</option>
                    {availableTargetQuestions.map(q => (
                      <option key={q.id} value={q.id}>
                        {q.question}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button 
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowBuilder(false)}
              >
                취소
              </button>
              <button 
                type="button"
                className="btn btn-primary"
                onClick={handleSaveLogic}
                disabled={!isFormValid()}
              >
                {editingLogic ? '수정' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConditionalLogicBuilder;